import React, { useState, useEffect } from 'react';
import { FingotiButton, FingotiLoading, FingotiModalContent, FingotiModalActions } from '@fingoti/components';
import { useSnackbar } from 'notistack';

import makeStyles from '@material-ui/core/styles/makeStyles';
import FormControl from '@material-ui/core/FormControl';
import TextField from '@material-ui/core/TextField';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';
import Collapse from '@material-ui/core/Collapse';
import Typography from '@material-ui/core/Typography';
import { DateTimePicker } from '@material-ui/pickers';

import { TokenScopeSelector } from './TokenScopeSelector';

const useStyles = makeStyles((theme) => ({

    root: {
        display: 'flex',
        justifyContent: 'center',
    },
    form: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        [theme.breakpoints.up('md')]: {
            width: '60%',
        }
    },
    formControl: {
        marginTop: theme.spacing(3),
    },
    scopesLabel: {
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(1),
        color: theme.palette.greyTwo.main,
    },
    buttonGroup: {
        display: 'flex',
        justifyContent: 'space-evenly',
        width: '100%',
        [theme.breakpoints.up('md')]: {
            width: '50%',
        }
    },
    button: {
        width: '42%',
    },
    expiry: {
        width: '100%',
        marginTop: theme.spacing(3),
    }

}))

export const UserTokenEdit = ({ update, handleEditToken, setOpen, token }) => {

    const classes = useStyles()
    const { enqueueSnackbar } = useSnackbar();
    const [thisLoading, setThisLoading] = useState(true);
    const [name, setName] = useState('');
    const [scopes, setScopes] = useState([]);
    const [enableExpiry, setEnableExpiry] = useState(false);
    const [expiry, setExpiry] = useState(new Date());
    const [enabled, setEnabled] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!token) {
            return;
        }
        setEnabled(!token.disabled);
        setName(token.name);
        setScopes(token.scopes ?? []);
        if (token.expiresOn !== null && token.expiresOn !== undefined) {
            setEnableExpiry(true);
            setExpiry(new Date(token.expiresOn));
        }
        setThisLoading(false);
    }, [token])

    const editToken = async () => {
        setSubmitting(true);
        try {
            await update(token.id, {
                name,
                scopes,
                disabled: !enabled,
                expiresOn: enableExpiry ? new Date(expiry).toISOString() : null,
            });
            enqueueSnackbar('Token updated', { variant: 'success' });
            handleEditToken();
        } catch (error) {
            enqueueSnackbar(error?.message ?? 'Failed to update token', { variant: 'error' });
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <>
            <FingotiModalContent>
                {thisLoading ?
                    <FingotiLoading />
                    :
                    <div className={classes.root}>
                        <form className={classes.form}>
                            <FormControl variant="outlined">
                                <TextField
                                    required
                                    id='tokenName'
                                    name='tokenName'
                                    variant='outlined'
                                    label='token name'
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                />
                            </FormControl>
                            <FormControl variant='outlined' className={classes.formControl}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={enabled}
                                            onChange={() => setEnabled(!enabled)}
                                            name='tokenEnabled'
                                            color='primary'
                                        />
                                    }
                                    label='token enabled?'
                                />
                            </FormControl>
                            <FormControl variant='outlined' className={classes.formControl}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={enableExpiry}
                                            onChange={() => setEnableExpiry(!enableExpiry)}
                                            name='enableExpiry'
                                            color='primary'
                                        />
                                    }
                                    label='enable expiry?'
                                />
                            </FormControl>
                            <Collapse in={enableExpiry}>
                                <FormControl variant='outlined' className={classes.expiry}>
                                    <DateTimePicker
                                        DialogProps={{ style: { zIndex: 1400 } }}
                                        value={expiry}
                                        hideTabs
                                        disablePast
                                        inputVariant='outlined'
                                        onChange={setExpiry}
                                        label="expiry date/time"
                                    />
                                </FormControl>
                            </Collapse>
                            <Typography variant='body2' className={classes.scopesLabel}>
                                scopes
                            </Typography>
                            <TokenScopeSelector value={scopes} onChange={setScopes} />
                        </form>
                    </div>
                }
            </FingotiModalContent>

            {!thisLoading &&
                <FingotiModalActions>
                    <div className={classes.buttonGroup}>
                        <FingotiButton
                            light
                            color="primary"
                            disabled={submitting || name.trim() === ''}
                            className={classes.button}
                            onClick={editToken}
                        >
                            save token
                        </FingotiButton>
                        <FingotiButton
                            light
                            className={classes.button}
                            onClick={() => setOpen(false)}
                        >
                            Cancel
                        </FingotiButton>
                    </div>
                </FingotiModalActions>
            }
        </>
    );

}
